import { nextTick } from "vue";

/**
 * The slice of Vuetify's VForm instance this helper needs. Structural on purpose: a Vue function ref
 * hands over whatever the element resolved to, and a test stub with a `validate` method qualifies too.
 */
export interface ValidatableForm {
	validate: () => Promise<{ valid: boolean }>;
}

/**
 * The control that owns the red state, inside the first field Vuetify has marked invalid. Selector
 * order carries no priority — querySelector returns the first match in DOCUMENT order — so this
 * relies on the field's input preceding anything else focusable inside `.v-input--error`, which holds
 * for every Vuetify field type the renderer produces.
 */
const INVALID_CONTROL_SELECTOR = ".v-input--error input, .v-input--error textarea, .v-input--error select";

/** Keeps only the things a function ref can hand over that are actually able to validate. */
export function isValidatableForm(candidate: unknown): candidate is ValidatableForm {
	return typeof (candidate as ValidatableForm | null | undefined)?.validate === "function";
}

/**
 * Make a refused submit VISIBLE (LSP-19461).
 *
 * The action button stays enabled and no-ops on an invalid form (CLAUDE.md, "Form action buttons —
 * always enabled, no-op when invalid"), and that rule says the feedback comes from the fields' own red
 * state. But Vuetify validates on INPUT: a required field nobody has touched has no red state to give,
 * so the click did nothing and the screen showed nothing. Running every form's `validate()` is what
 * makes the rules paint; focusing the first painted control is what tells the user where to look.
 *
 * Duplicated between contact-admin and the shared realtime-analysis package for the same reason the
 * commit barrier is — admin cannot import from `shared/` — and the parity guard pins the copies.
 *
 * @returns the control that received focus, or null when nothing rendered as invalid.
 */
export async function revealInvalidUserInput(
	forms: Iterable<ValidatableForm>,
	container: HTMLElement | null | undefined
): Promise<HTMLElement | null> {
	await Promise.all([...forms].map((form) => form.validate()));
	// The error class lands on the next render, not inside validate().
	await nextTick();
	const control = container?.querySelector<HTMLElement>(INVALID_CONTROL_SELECTOR) ?? null;
	if (!control) return null;
	if (typeof control.scrollIntoView === "function") control.scrollIntoView({ block: "center" });
	control.focus();
	return control;
}
